import React, { useState, useEffect } from 'react';
import { Loader } from 'semantic-ui-react';
import PropTypes from 'prop-types';

/**
 * TimedLoader Component
 * 
 * A loader that shows initial loading text, then after a timeout either:
 * - Shows different loader text (if afterTimeoutText is provided)
 * - Shows custom content (if afterTimeout is provided)
 * - Hides completely (if neither is provided)
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.active - Whether the loader is active
 * @param {string} props.initialText - Initial loading text
 * @param {number} props.timeout - Milliseconds before changing state (default: 3000)
 * @param {string} props.afterTimeoutText - Text to show in loader after timeout
 * @param {React.ReactNode} props.afterTimeout - Custom content to show after timeout
 * @param {Function} props.onTimeout - Callback when timeout occurs
 * All other props are passed through to Semantic UI Loader
 */
const TimedLoader = (props) => {
  const {
    active = true,
    initialText = 'Loading...',
    timeout = 3000,
    afterTimeoutText,
    afterTimeout,
    onTimeout,
    ...loaderProps
  } = props;
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (!active) {
      // Reset state if loader becomes inactive
      setHasTimedOut(false);
      return;
    }

    // Set up the timeout
    const timer = setTimeout(() => {
      setHasTimedOut(true);
      if (onTimeout) {
        onTimeout();
      }
    }, timeout);

    // Cleanup function
    return () => clearTimeout(timer);
  }, [active, timeout, onTimeout]);

  // If not active, don't render anything
  if (!active) {
    return null;
  }

  // After timeout, show custom content if provided
  if (hasTimedOut && afterTimeout) {
    return afterTimeout;
  }

  // After timeout, show loader with different text if provided
  if (hasTimedOut && afterTimeoutText) {
    return (
      <Loader active {...loaderProps}>
        {afterTimeoutText}
      </Loader>
    );
  }

  // After timeout with no afterTimeout or afterTimeoutText, hide completely
  if (hasTimedOut) {
    return null;
  }

  // Before timeout, show initial loader
  return (
    <Loader active {...loaderProps}>
      {initialText}
    </Loader>
  );
};

TimedLoader.propTypes = {
  active: PropTypes.bool,
  initialText: PropTypes.string,
  timeout: PropTypes.number,
  afterTimeoutText: PropTypes.string,
  afterTimeout: PropTypes.node,
  onTimeout: PropTypes.func,
  // Semantic UI Loader props
  size: PropTypes.oneOf(['mini', 'tiny', 'small', 'medium', 'large', 'big', 'huge', 'massive']),
  inline: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['centered'])]),
  inverted: PropTypes.bool,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool
};

export default TimedLoader;
